import * as React from 'react'
import { FaIcon, faXmark, faCircleInfo, faChevronRight } from '@/lib/fa'
import { Portal } from '@/ui/overlays/Portal'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import type { TimesheetModel } from '@/app/models/timesheetModel'

interface TimesheetExplainDrawerProps {
  isOpen: boolean
  onClose: () => void
  timesheet: TimesheetModel | null
  onExplainRow?: (rowKey: string) => void
}

export const TimesheetExplainDrawer: React.FC<TimesheetExplainDrawerProps> = ({
  isOpen,
  onClose,
  timesheet,
  onExplainRow,
}) => {
  const { t, tSafe } = useLocalize()

  const rows = timesheet?.rows ?? []

  if (!isOpen) return null

  return (
    <Portal layer="modal">
      <div
        className="tw-fixed tw-inset-0 tw-z-[200] tw-bg-content-base/40 tw-font-primary"
        onClick={onClose}
      >
        <aside
          className="tw-fixed tw-bottom-0 tw-right-0 tw-top-0 tw-flex tw-w-[480px] tw-max-w-[100vw] tw-flex-col tw-bg-base-100 tw-shadow-xl"
          onClick={(e) => e.stopPropagation()}
          aria-labelledby="explain-drawer-title"
        >
          {/* Header — mirrors the app's modal header pattern (AddDatabaseModal). */}
          <header className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-6 tw-py-4">
            <div className="tw-flex tw-items-center tw-gap-3">
              <span className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-bg-content-background">
                <FaIcon
                  icon={faCircleInfo}
                  className="tw-h-4 tw-w-4 tw-text-primary"
                />
              </span>
              <h2
                id="explain-drawer-title"
                className="tw-text-xl tw-font-semibold tw-text-content-base"
              >
                {tSafe('explain.drawer.title', 'Why these numbers?')}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-md tw-text-content-base/60 hover:tw-bg-base-200 hover:tw-text-content-base"
              aria-label={t('common.close')}
            >
              <FaIcon icon={faXmark} className="tw-h-4 tw-w-4" />
            </button>
          </header>

          {/* Body */}
          <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5">
            <p className="tw-mb-5 tw-text-sm tw-leading-5 tw-text-content-base/70">
              {tSafe(
                'explain.drawer.intro-rows',
                'Click a row to see which company settings shape its values. For the full settings overview, generate the calculation audit printout.',
              )}
            </p>


            <ul className="tw-flex tw-flex-col tw-gap-2">
              {rows.map((row) => {
                if (!row.titleKey) return null
                const isClickable = !!onExplainRow
                const ruleCount = row.appliedRuleKeys?.length ?? 0
                const title = tSafe(
                  `explain.row.${row.titleKey}.title`,
                  row.title,
                )
                const description = tSafe(
                  `explain.row.${row.titleKey}.description`,
                  tSafe(
                    'explain.row.fallback-description',
                    "Calculated from the driver's activities and the company settings shown above.",
                  ),
                )
                return (
                  <li key={row.titleKey + row.title}>
                    <button
                      type="button"
                      disabled={!isClickable}
                      onClick={() => onExplainRow?.(row.titleKey!)}
                      className={`tw-group tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-lg tw-border tw-border-base-300 tw-bg-base-100 tw-px-3 tw-py-3 tw-text-left tw-transition-colors ${
                        isClickable
                          ? 'hover:tw-border-primary hover:tw-bg-content-background'
                          : 'tw-cursor-default'
                      }`}
                    >
                      <span className="tw-mt-0.5 tw-grid tw-h-7 tw-w-7 tw-flex-none tw-place-items-center tw-rounded-md tw-bg-content-background tw-text-primary group-hover:tw-bg-base-100">
                        <FaIcon
                          icon={faCircleInfo}
                          className="tw-h-3.5 tw-w-3.5"
                        />
                      </span>
                      <span className="tw-min-w-0 tw-flex-1">
                        <span className="tw-flex tw-items-center tw-gap-2">
                          <span className="tw-text-sm tw-font-semibold tw-text-content-base">
                            {title}
                          </span>
                          {ruleCount > 0 && (
                            <span className="tw-rounded-full tw-bg-content-background tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-medium tw-text-primary">
                              {ruleCount}
                            </span>
                          )}
                        </span>
                        <span className="tw-mt-1 tw-block tw-text-xs tw-leading-4 tw-text-content-base/60">
                          {description}
                        </span>
                      </span>
                      {isClickable && (
                        <FaIcon
                          icon={faChevronRight}
                          className="tw-mt-2 tw-h-3 tw-w-3 tw-flex-none tw-text-content-base/40 group-hover:tw-text-primary"
                        />
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </aside>
      </div>
    </Portal>
  )
}

export default TimesheetExplainDrawer
